// /invites/:inviteId?token=… — the link an owner's invite emails (TOM-458, ADR
// 0319). Whoever holds the link sees who invited them to which boat, and can
// decline without an account. Accepting takes an account signed in with the
// invited address: signed out, the page sends them to sign in or sign up and
// brings them back here; signed in as someone else, it says so and offers to
// switch.
//
// The page sits outside RequireAuth so the invite reads before sign-in. The
// return trip keeps the token in `from`: router state for the password forms,
// and the Google start's `from`, which allows /invites (oidc.ts).

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth';
import {
  acceptInvite,
  closedInviteMessage,
  declineInvite,
  ownersErrorMessage,
  previewInvite,
  type InvitePreview,
} from '../lib/boatOwners';
import { useDocumentTitle } from '../lib/useDocumentTitle';

type Load = { kind: 'loading' } | { kind: 'missing' } | { kind: 'ready'; preview: InvitePreview };

export function InviteAccept() {
  useDocumentTitle('Your invite - Neomaris');
  const { inviteId = '' } = useParams<{ inviteId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const auth = useAuth();
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [declined, setDeclined] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (inviteId === '' || token === '') {
      setLoad({ kind: 'missing' });
      return;
    }
    let live = true;
    previewInvite(inviteId, token)
      .then((preview) => {
        if (!live) return;
        // A body that is not an invite reads as a link that does not work.
        const ok = typeof preview?.email === 'string' && typeof preview?.state === 'string';
        setLoad(ok ? { kind: 'ready', preview } : { kind: 'missing' });
      })
      .catch(() => {
        if (live) setLoad({ kind: 'missing' });
      });
    return () => {
      live = false;
    };
  }, [inviteId, token]);

  const here = `/invites/${encodeURIComponent(inviteId)}?token=${encodeURIComponent(token)}`;

  async function onAccept() {
    setBusy('accept');
    setError(null);
    try {
      const { boatId } = await acceptInvite(inviteId, token);
      navigate(`/dashboard/boats/${boatId}`, { replace: true });
    } catch (err) {
      setError(ownersErrorMessage(err, 'Could not accept the invite. Try again.'));
      setBusy(null);
    }
  }

  async function onDecline() {
    setBusy('decline');
    setError(null);
    try {
      await declineInvite(inviteId, token);
      setDeclined(true);
    } catch (err) {
      setError(ownersErrorMessage(err, 'Could not decline the invite. Try again.'));
    } finally {
      setBusy(null);
    }
  }

  if (load.kind === 'loading' || auth.status === 'unknown') {
    return (
      <Card title="Your invite">
        <p className="auth-sub">Loading…</p>
      </Card>
    );
  }

  if (load.kind === 'missing') {
    return (
      <Card title="This invite link doesn’t work">
        <p className="auth-sub" data-testid="invite-missing">
          The link is incomplete, or the invite no longer exists. Ask an owner of the boat to send a
          new one.
        </p>
      </Card>
    );
  }

  const { preview } = load;
  const boat = preview.boatName?.trim() || 'a boat';
  const who = preview.invitedBy?.trim() || 'An owner';

  if (declined) {
    return (
      <Card title="Invite declined">
        <p className="auth-sub" data-testid="invite-declined">
          You won’t be added to {boat}. Nothing else changes.
        </p>
      </Card>
    );
  }

  if (preview.state !== 'open') {
    return (
      <Card title={`Your invite to ${boat}`}>
        <p className="auth-sub" data-testid="invite-closed">
          {closedInviteMessage(preview.state)}
        </p>
        {auth.isAuthenticated ? (
          <Link to="/dashboard" className="btn btn-primary auth-submit">
            Go to your boats
          </Link>
        ) : null}
      </Card>
    );
  }

  const signedInAs = auth.user?.email ?? null;
  const rightAccount =
    signedInAs !== null && signedInAs.toLowerCase() === preview.email.toLowerCase();

  return (
    <Card title={`Join ${boat}`}>
      <p className="auth-sub" data-testid="invite-summary">
        {who} invited <strong>{preview.email}</strong> to be an owner of <strong>{boat}</strong>.
        Owners see the boat’s live data, history, alarms and boards, and get its alarms on their
        phone. Your chats with Neomaris, your notes and your settings stay your own.
      </p>

      {!auth.isAuthenticated ? (
        <>
          <p className="auth-sub">Sign in, or create an account, with {preview.email} to accept.</p>
          <div className="invite-page__actions">
            <Link
              to="/login"
              state={{ from: here, email: preview.email }}
              className="btn btn-primary"
              data-testid="invite-sign-in"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              state={{ from: here, email: preview.email }}
              className="btn"
              data-testid="invite-sign-up"
            >
              Create an account
            </Link>
          </div>
        </>
      ) : rightAccount ? (
        <div className="invite-page__actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy !== null}
            data-testid="invite-accept"
            onClick={() => void onAccept()}
          >
            {busy === 'accept' ? 'Joining…' : `Join ${boat}`}
          </button>
        </div>
      ) : (
        <>
          <p className="auth-sub" data-testid="invite-wrong-account">
            You’re signed in as {signedInAs}. This invite is for {preview.email}: sign in with that
            address to accept it.
          </p>
          <div className="invite-page__actions">
            <button
              type="button"
              className="btn btn-primary"
              data-testid="invite-switch-account"
              onClick={() => void auth.logout()}
            >
              Sign out and switch account
            </button>
          </div>
        </>
      )}

      <div className="invite-page__actions">
        <button
          type="button"
          className="btn"
          disabled={busy !== null}
          data-testid="invite-decline"
          onClick={() => void onDecline()}
        >
          {busy === 'decline' ? 'Declining…' : 'Decline'}
        </button>
      </div>

      {error !== null ? (
        <div className="auth-banner auth-banner--error" role="alert" data-testid="invite-error">
          {error}
        </div>
      ) : null}
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="auth-panel">
      <div className="auth-card auth-card-v2" data-testid="invite-page">
        <h2>{title}</h2>
        {children}
      </div>
    </section>
  );
}
